/**
 * TestGridPage - Prototype page for UnifiedDataGrid testing
 *
 * This page demonstrates the UnifiedDataGrid with mock data,
 * showing inline editing, mass updates, and cache synchronization.
 *
 * Route: /test-grid
 */

import React, { useCallback, useMemo, useState } from 'react'
import { Box, Button, Paper, Typography, Divider, Alert } from '@mui/material'
import { useGridApiRef, GridColDef } from '@mui/x-data-grid-premium'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { UnifiedDataGrid } from './UnifiedDataGrid'
import { TEST_ENTITY_CONFIG } from './config/entityConfigs'
import { useMockGridData } from './mockData/useMockGridData'
import {
  createTextColumn,
  createSelectColumn,
  createTeamMemberColumn,
  createUserColumn,
  createContactColumn,
  createOrgReferenceColumn,
  createCurrencyColumn,
  createDateColumn,
} from './columns/columnFactories'

export const TestGridPage: React.FC = () => {
  const apiRef = useGridApiRef()
  const { showSnackbar } = useSnackbarContext()
  const [gridKey, setGridKey] = useState(0)

  // Use mock data hook
  const {
    rows,
    users,
    contacts,
    organizations,
    statusOptions,
    typeOptions,
    verticalOptions,
    applyUpdates,
    deleteRows,
    reset,
  } = useMockGridData()

  // Handle reset with grid remount
  const handleReset = useCallback(() => {
    reset()
    setGridKey(k => k + 1) // Force grid remount
    showSnackbar('Data reset to initial state', 'info')
  }, [reset, showSnackbar])

  // Grid options for column factories
  const gridOptions = useMemo(
    () => ({
      users,
      contacts,
      organizations,
    }),
    [users, contacts, organizations]
  )

  // Build columns using factories
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Organization',
        width: 200,
        editable: false,
      },
      createTextColumn('website', 'Website', {
        editable: true,
        width: 200,
        inputType: 'url',
        placeholder: 'https://...',
      }),
      createSelectColumn('status', 'Status', statusOptions, {
        editable: true,
        width: 120,
        getOptionColor: opt => opt.color,
      }),
      createSelectColumn('type', 'Type', typeOptions, {
        editable: true,
        width: 100,
      }),
      createSelectColumn('vertical', 'Vertical', verticalOptions, {
        editable: true,
        width: 130,
      }),
      createUserColumn('owner', 'Owner', gridOptions, {
        width: 150,
      }),
      createContactColumn('primaryContact', 'Primary Contact', gridOptions, {
        width: 150,
        multiple: false,
      }),
      createTeamMemberColumn('team.AM', 'AM', gridOptions, {
        width: 80,
      }),
      createTeamMemberColumn('team.PM', 'PM', gridOptions, {
        width: 80,
      }),
      createOrgReferenceColumn('contractedBy', 'Contracted By', gridOptions, {
        width: 180,
        multiple: true,
      }),
      createCurrencyColumn('monthlySpend', 'Monthly Spend', {
        width: 130,
      }),
      createDateColumn('createdAt', 'Created', {
        width: 110,
      }),
    ],
    [gridOptions, statusOptions, typeOptions, verticalOptions]
  )

  // Handle mass update completion
  const handleMassUpdateComplete = useCallback(
    (count: number, field: string, value: any) => {
      console.log(`[TestGridPage] Mass update complete: ${count} rows, field: ${field}`, value)
    },
    []
  )

  // Handle delete
  const handleDeleteClick = useCallback(
    (selectedIds: Set<string | number>) => {
      const count = selectedIds.size
      if (window.confirm(`Are you sure you want to delete ${count} organization(s)?`)) {
        deleteRows(selectedIds)
        showSnackbar(`Deleted ${count} organization(s)`, 'success')
      }
    },
    [deleteRows, showSnackbar]
  )

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2, gap: 2 }}>
      {/* Header */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h5">UnifiedDataGrid Prototype</Typography>
          <Button variant="outlined" onClick={handleReset} size="small">
            Reset Data
          </Button>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Alert severity="info" sx={{ mt: 1 }}>
          <Typography variant="body2">
            <strong>Try it out:</strong>
          </Typography>
          <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
            <li>
              <strong>Single edit:</strong> Click any editable cell to edit it
            </li>
            <li>
              <strong>Mass edit:</strong> Select multiple rows using checkboxes, then edit any editable
              cell - all selected rows will update
            </li>
            <li>
              <strong>Editable columns:</strong> Website, Status, Type, Vertical, Owner, Primary Contact,
              AM, PM, Contracted By
            </li>
            <li>
              <strong>Mass update cells are highlighted</strong> in blue when multiple rows are selected
            </li>
          </ul>
        </Alert>
      </Paper>

      {/* Grid */}
      <Paper sx={{ flex: 1, minHeight: 0 }}>
        <UnifiedDataGrid
          key={gridKey}
          config={TEST_ENTITY_CONFIG}
          rows={rows}
          columns={columns}
          apiRef={apiRef}
          options={gridOptions}
          mockMode={true}
          onMockUpdate={applyUpdates}
          onMassUpdateComplete={handleMassUpdateComplete}
          onDeleteClick={handleDeleteClick}
          showSnackbar={showSnackbar}
          checkboxSelection={true}
          gridSxProps={{
            border: 'none',
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
          }}
        />
      </Paper>
    </Box>
  )
}

export default TestGridPage
